import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LabelList,
  LineChart,
  Line,
  ComposedChart,
} from 'recharts';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import { listMonthlyTargets } from '../../services/monthlyTargetService';

const PageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.section`
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  padding: 24px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const FilterTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const FilterSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
`;

const FilterStack = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 4h16l-6 7v6l-4 3v-9z" />
  </svg>
);

const FilterForm = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 18px;
`;

const FilterControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #475569;
  font-weight: 700;
`;

const SelectInput = styled.select`
  border: 1px solid #d0d7ff;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  font-size: 14px;
  background: #f8fafc;
`;

const DateInput = styled.input`
  border: 1px solid #d0d7ff;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  font-size: 14px;
  background: #f8fafc;
`;

const ActionGroup = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #ffffff;
  background: linear-gradient(120deg, #2563eb, #3b82f6);
  box-shadow: 0 15px 30px rgba(37, 99, 235, 0.25);
`; 

const ExportButton = styled(ActionButton)`
  background: linear-gradient(120deg, #16a34a, #22c55e);
  box-shadow: 0 15px 30px rgba(34, 197, 94, 0.25);
`;

const AppliedFiltersCard = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const AppliedFiltersTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const FilterPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #312e81;
  font-size: 12px;
  font-weight: 600;
`;

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChartHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChartHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  align-items: flex-start;
`;

const ChartHeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  flex: 1;
`;

const ChartFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;
`;

const ChartFilterLabel = styled.label`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #475569;
  font-weight: 700;
`;

const ChartSelect = styled.select`
  border: 1px solid #d0d7ff;
  border-radius: 10px;
  padding: 0 12px;
  height: 42px;
  font-size: 14px;
  background: #f8fafc;
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const ChartSubtitle = styled.p`
  margin: 0;
  color: #475569;
  font-size: 14px;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 360px;
`;

const ChartEmpty = styled.div`
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
`;

const CourseHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
`;

const CourseTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CourseTitleText = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CourseMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 13px;
  color: #475569;
`;

const CourseMetaItem = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: baseline;
`;

const CourseMetaValue = styled.span`
  font-weight: 600;
  color: #0f172a;
`;

const CourseBadge = styled.span`
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-weight: 700;
  padding: 8px 16px;
  font-size: 14px;
`;

const CourseIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const CourseChartWrapper = styled.div`
  width: 100%;
  height: 420px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;
`;

const DetailHeadCell = styled.th`
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
`;

const DetailRow = styled.tr`
  border-bottom: 1px solid #edf2f7;
`;

const DetailCell = styled.td`
  padding: 16px;
  vertical-align: top;
  font-size: 14px;
  color: #0f172a;
  border-bottom: 1px solid #edf2f7;
  background: #ffffff;
`;

const StaffDetailHeadCell = styled(DetailHeadCell)`
  width: 360px;
`;

const StaffDetailCell = styled(DetailCell)`
  width: 360px;
`;

const StaffIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StaffImageWrapper = styled.span`
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #0f172a;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  overflow: hidden;
  flex-shrink: 0;
`;

const StaffImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StaffInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StaffMeta = styled.span`
  font-size: 13px;
  color: #475569;
`;

const CourseList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CourseItem = styled.li`
  font-size: 13px;
  color: #0f172a;
  line-height: 1.4;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 16px;
`;

const ModalPanel = styled.div`
  width: min(520px, 100%);
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 35px 90px rgba(15, 23, 42, 0.35);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  color: #475569;
`;

const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const STATUS_TONES = {
  success: { bg: '#dcfce7', color: '#166534' },
  good: { bg: '#fef9c3', color: '#854d0e' },
  caution: { bg: '#ffedd5', color: '#9a3412' },
  alert: { bg: '#fee2e2', color: '#991b1b' },
  neutral: { bg: '#e2e8f0', color: '#475569' },
};

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => STATUS_TONES[props.$tone]?.bg || STATUS_TONES.neutral.bg};
  color: ${(props) => STATUS_TONES[props.$tone]?.color || STATUS_TONES.neutral.color};
`;

const CourseToggleButton = styled.button`
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #3730a3;
  background: #eef2ff;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #e0e7ff;
  }
`;


const PlaceholderPanel = styled(Card)`
  text-align: center;
  color: #475569;
`;

const PlaceholderTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  color: #0f172a;
`;

const PlaceholderText = styled.p`
  margin: 0;
  font-size: 14px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
`;

const MetricCard = styled.div`
  border-radius: 16px;
  border: 1px solid ${(p) => p.$accentBorder || '#cbd5f5'};
  background: #ffffff;
  padding: 20px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MetricIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$accentBg || '#eff6ff'};
  color: ${(p) => p.$accent || '#2563eb'};
`;

const MetricValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
`;

const MetricLabel = styled.span`
  font-size: 14px;
  color: #1e293b;
`;

const MetricHint = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const ErrorBanner = styled.div`
  border-radius: 14px;
  border: 1px solid #fecdd3;
  background: #fff1f2;
  color: #b91c1c;
  padding: 12px 16px;
`;

const quarterOptions = [
  { value: 'Q1', label: 'Q1 (Apr-Jun)' },
  { value: 'Q2', label: 'Q2 (Jul-Sep)' },
  { value: 'Q3', label: 'Q3 (Oct-Dec)' },
  { value: 'Q4', label: 'Q4 (Jan-Mar)' },
];

const selectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: '12px',
    borderColor: '#d0d7ff',
    minHeight: '48px',
    background: '#f8fafc',
    fontSize: '14px',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#f1f5f9',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#0f172a',
  }),
};

const buildFinancialYearOptions = () => {
  const today = new Date();
  const monthIndex = today.getMonth();
  const currentYear = today.getFullYear();
  const fiscalStartYear = monthIndex >= 3 ? currentYear : currentYear - 1;
  const firstYear = fiscalStartYear - 2;
  const years = [];
  for (let index = 0; index < 6; index += 1) {
    const start = firstYear + index;
    const end = start + 1;
    years.push({ value: `${start}-${end}`, label: `${start}-${end}` });
  }
  return years.reverse();
};

const formatDateLabel = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRangeLabel = (start, end) => {
  if (start && end) {
    return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
  }
  if (start) {
    return `From ${formatDateLabel(start)}`;
  }
  if (end) {
    return `Through ${formatDateLabel(end)}`;
  }
  return 'Full financial year';
};

const numberFormatter = new Intl.NumberFormat('en-US');
const currencyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatNumber = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '—';
  }
  return numberFormatter.format(parsed);
};

const formatCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '—';
  }
  return `Rs. ${currencyFormatter.format(parsed)}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const rounded = Number(value.toFixed(1));
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}%`;
};

const formatCurrencyShort = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '—';
  }
  if (Math.abs(parsed) >= 1_000_000) {
    return `Rs. ${(parsed / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(parsed) >= 1_000) {
    return `Rs. ${(parsed / 1_000).toFixed(1)}K`;
  }
  return formatCurrency(parsed);
};

const formatMonthKeyLabel = (key) => {
  if (!key) {
    return '—';
  }
  const [year, month] = key.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return key;
  }
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const getStaffInitials = (name) => {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '?';
  }
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('') || '?';
};

const aggregateStaffPerformance = (records = []) => {
  const map = new Map();

  records.forEach((record) => {
    if (!record) {
      return;
    }
    const staffId = record?.staff?.id || record.staffCourseMappingId || record.id;
    const staffName = record?.staff?.name || 'Unknown';
    if (!map.has(staffId)) {
      map.set(staffId, {
        staffId,
        staffName,
        expectedIncome: 0,
        actualIncome: 0,
      });
    }
    const entry = map.get(staffId);
    const expected = Number(record?.expectation?.income);
    if (Number.isFinite(expected)) {
      entry.expectedIncome += expected;
    }
    const actual = Number(record?.actuals?.income);
    if (Number.isFinite(actual)) {
      entry.actualIncome += actual;
    }
  });

  return Array.from(map.values()).sort((a, b) => (b.expectedIncome || 0) - (a.expectedIncome || 0));
};

const aggregateMonthlyTrends = (records = []) => {
  const map = new Map();

  records.forEach((record) => {
    const monthKey = record?.period?.key;
    if (!monthKey) {
      return;
    }
    if (!map.has(monthKey)) {
      const [year, month] = monthKey.split('-').map(Number);
      const sortValue = Number.isFinite(year) && Number.isFinite(month)
        ? new Date(year, month - 1, 1).getTime()
        : Number.POSITIVE_INFINITY;
      map.set(monthKey, {
        monthKey,
        label: formatMonthKeyLabel(monthKey),
        sortValue,
        expectedIncome: 0,
        actualIncome: 0,
      });
    }
    const entry = map.get(monthKey);
    const expected = Number(record?.expectation?.income);
    if (Number.isFinite(expected)) {
      entry.expectedIncome += expected;
    }
    const actual = Number(record?.actuals?.income);
    if (Number.isFinite(actual)) {
      entry.actualIncome += actual;
    }
  });

  return Array.from(map.values())
    .map((entry) => ({
      ...entry,
      achievementRate: entry.expectedIncome > 0 && Number.isFinite(entry.actualIncome)
        ? (entry.actualIncome / entry.expectedIncome) * 100
        : null,
    }))
    .sort((a, b) => {
      if (a.sortValue !== b.sortValue) {
        return a.sortValue - b.sortValue;
      }
      return a.monthKey > b.monthKey ? 1 : -1;
    });
};

const resolveAchievementStatus = (rate) => {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) {
    return { label: 'Pending', tone: 'neutral' };
  }
  if (rate >= 110) {
    return { label: 'Outstanding', tone: 'success' };
  }
  if (rate >= 85) {
    return { label: 'Good', tone: 'good' };
  }
  if (rate >= 70) {
    return { label: 'Caution', tone: 'caution' };
  }
  return { label: 'Needs action', tone: 'alert' };
};

const UsersIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const TrophyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <path d="M7 4v5a5 5 0 0 0 5 5 5 5 0 0 0 5-5V4" />
    <path d="M5 4h14" />
    <path d="M5 8H3a2 2 0 0 1 0-4h2" />
    <path d="M19 8h2a2 2 0 0 0 0-4h-2" />
  </svg>
);

const ChartIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 19V9" />
    <path d="M10 19V5" />
    <path d="M16 19v-8" />
    <path d="M22 19V7" />
  </svg>
);

export default function AdminSalesTargetSummary() {
  const financialYearOptions = useMemo(() => buildFinancialYearOptions(), []);
  const [filters, setFilters] = useState(() => ({
    financialYear: financialYearOptions[0]?.value || '',
    quarters: [],
    startDate: '',
    endDate: '',
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    financialYear: financialYearOptions[0]?.value || '',
    quarters: [],
    startDate: '',
    endDate: '',
  }));
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [staffSummary, setStaffSummary] = useState([]);
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [trendCourseFilter, setTrendCourseFilter] = useState('all');
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState('');
  const [courseModalData, setCourseModalData] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError('');
      const response = await listMonthlyTargets({ page: 1, pageSize: 1 });
      setSummaryData(response?.summary || null);
    } catch (error) {
      setSummaryData(null);
      setSummaryError(error?.response?.data?.message || 'Unable to load summary data.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadPerformanceData = useCallback(async () => {
    try {
      setPerformanceLoading(true);
      setPerformanceError('');
      const pageSize = 250;
      const maxPages = 10;
      let page = 1;
      let totalPages = 1;
      const allRecords = [];

      do {
        const response = await listMonthlyTargets({ page, pageSize, sortBy: 'staff', sortDir: 'asc' });
        allRecords.push(...(response?.data || []));
        totalPages = response?.meta?.totalPages || 1;
        page += 1;
      } while (page <= totalPages && page <= maxPages);

      setPerformanceRecords(allRecords);
      setStaffSummary(aggregateStaffPerformance(allRecords));
    } catch (error) {
      setStaffSummary([]);
      setPerformanceRecords([]);
      setPerformanceError(error?.response?.data?.message || 'Unable to load performance insights.');
    } finally {
      setPerformanceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
    loadPerformanceData();
  }, [loadSummary, loadPerformanceData]);

  const quarterText = appliedFilters.quarters.length
    ? appliedFilters.quarters.map((quarter) => quarter.label).join(', ')
    : 'All quarters';

  const metricCards = useMemo(() => {
    if (!summaryData) {
      return [];
    }

    const expectedIncomeValue = Number(summaryData.expectedIncome ?? 0);
    const actualIncomeValue = summaryData.actualIncome === null || summaryData.actualIncome === undefined
      ? null
      : Number(summaryData.actualIncome);
    const achievementRateValue = expectedIncomeValue > 0 && actualIncomeValue !== null
      ? (actualIncomeValue / expectedIncomeValue) * 100
      : null;

    return [
      {
        label: 'Total Staff',
        value: formatNumber(summaryData.uniqueStaff || 0),
        hint: 'Counselors with active targets',
        accent: '#2563eb',
        accentBg: 'rgba(37, 99, 235, 0.12)',
        accentBorder: 'rgba(37, 99, 235, 0.35)',
        icon: UsersIcon,
      },
      {
        label: 'Expected Income',
        value: formatCurrency(expectedIncomeValue),
        hint: 'Planned receivables',
        accent: '#0ea5e9',
        accentBg: 'rgba(14, 165, 233, 0.12)',
        accentBorder: 'rgba(14, 165, 233, 0.35)',
        icon: TrophyIcon,
      },
      {
        label: 'Actual Income',
        value: actualIncomeValue === null ? '—' : formatCurrency(actualIncomeValue),
        hint: actualIncomeValue === null ? 'Awaiting updates' : 'Recognized so far',
        accent: '#059669',
        accentBg: 'rgba(5, 150, 105, 0.12)',
        accentBorder: 'rgba(5, 150, 105, 0.35)',
        icon: TrophyIcon,
      },
      {
        label: 'Achievement Rate',
        value: formatPercent(achievementRateValue),
        hint: expectedIncomeValue > 0 ? 'Actual vs plan' : 'Set expectations first',
        accent: '#f59e0b',
        accentBg: 'rgba(245, 158, 11, 0.12)',
        accentBorder: 'rgba(245, 158, 11, 0.35)',
        icon: ChartIcon,
      },
    ];
  }, [summaryData]);

  const staffChartData = useMemo(() => (
    staffSummary.slice(0, 8).map((entry) => ({
      staffId: entry.staffId,
      staffName: entry.staffName,
      expectedIncome: entry.expectedIncome || 0,
      actualIncome: entry.actualIncome || 0,
    }))
  ), [staffSummary]);

  const monthlyTrendData = useMemo(() => {
    if (!performanceRecords.length) {
      return [];
    }
    const targetCourseId = trendCourseFilter === 'all' ? null : Number(trendCourseFilter);
    const filteredRecords = targetCourseId
      ? performanceRecords.filter((record) => Number(record?.courseCode?.id) === targetCourseId)
      : performanceRecords;
    if (!filteredRecords.length) {
      return [];
    }
    const aggregated = aggregateMonthlyTrends(filteredRecords);
    const limit = 12;
    return aggregated.slice(-limit);
  }, [performanceRecords, trendCourseFilter]);

  const trendCourseOptions = useMemo(() => {
    const baseOptions = [{ value: 'all', label: 'All courses' }];
    if (!performanceRecords.length) {
      return baseOptions;
    }
    const courseMap = new Map();
    performanceRecords.forEach((record) => {
      const courseId = record?.courseCode?.id;
      if (!courseId) {
        return;
      }
      if (!courseMap.has(courseId)) {
        const shortName = record?.courseCode?.shortName?.trim();
        const fullName = record?.courseCode?.fullName?.trim();
        courseMap.set(courseId, shortName || fullName || `Course ${courseId}`);
      }
    });
    const courseOptions = Array.from(courseMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, label]) => ({ value: String(id), label }));
    return [...baseOptions, ...courseOptions];
  }, [performanceRecords]);

  const selectedCourseOption = useMemo(
    () => trendCourseOptions.find((option) => option.value === trendCourseFilter),
    [trendCourseOptions, trendCourseFilter]
  );

  const selectedCourseId = selectedCourseOption && selectedCourseOption.value !== 'all'
    ? Number(selectedCourseOption.value)
    : null;

  const coursePerformanceRecords = useMemo(() => {
    if (!performanceRecords.length) {
      return [];
    }
    if (!selectedCourseId) {
      return performanceRecords;
    }
    return performanceRecords.filter((record) => Number(record?.courseCode?.id) === selectedCourseId);
  }, [performanceRecords, selectedCourseId]);

  const coursePerformanceSummary = useMemo(() => {
    if (!coursePerformanceRecords.length) {
      return null;
    }
    let expectedIncome = 0;
    let actualIncome = 0;
    let hasActualIncome = false;
    let expectedRegistrations = 0;
    let actualRegistrations = 0;
    let hasActualRegistrations = false;
    const staffSet = new Set();
    coursePerformanceRecords.forEach((record) => {
      const expectedInc = Number(record?.expectation?.income);
      if (Number.isFinite(expectedInc)) {
        expectedIncome += expectedInc;
      }
      const actualInc = Number(record?.actuals?.income);
      if (Number.isFinite(actualInc)) {
        actualIncome += actualInc;
        hasActualIncome = true;
      }
      const expectedReg = Number(record?.expectation?.registrations);
      if (Number.isFinite(expectedReg)) {
        expectedRegistrations += expectedReg;
      }
      const actualReg = Number(record?.actuals?.registrations);
      if (Number.isFinite(actualReg)) {
        actualRegistrations += actualReg;
        hasActualRegistrations = true;
      }
      const staffId = record?.staff?.id;
      if (staffId) {
        staffSet.add(staffId);
      }
    });
    const achievementRate = expectedIncome > 0 && hasActualIncome
      ? (actualIncome / expectedIncome) * 100
      : null;
    const courseName = selectedCourseId
      ? (coursePerformanceRecords[0]?.courseCode?.fullName
        || coursePerformanceRecords[0]?.courseCode?.shortName
        || selectedCourseOption?.label
        || 'Selected Course')
      : 'All Courses';
    return {
      courseName,
      expectedIncome,
      actualIncome: hasActualIncome ? actualIncome : null,
      expectedRegistrations,
      actualRegistrations: hasActualRegistrations ? actualRegistrations : null,
      staffCount: staffSet.size,
      achievementRate,
    };
  }, [coursePerformanceRecords, selectedCourseId, selectedCourseOption]);

  const coursePerformanceTrendData = useMemo(() => {
    if (!coursePerformanceRecords.length) {
      return [];
    }
    const aggregated = aggregateMonthlyTrends(coursePerformanceRecords);
    const limit = 12;
    return aggregated.slice(-limit);
  }, [coursePerformanceRecords]);

  useEffect(() => {
    if (trendCourseFilter === 'all') {
      return;
    }
    const hasValue = trendCourseOptions.some((option) => option.value === trendCourseFilter);
    if (!hasValue) {
      setTrendCourseFilter('all');
    }
  }, [trendCourseFilter, trendCourseOptions]);

  const staffDetailRows = useMemo(() => {
    if (!performanceRecords.length) {
      return [];
    }
    const map = new Map();
    performanceRecords.forEach((record) => {
      const staffId = record?.staff?.id;
      if (!staffId) {
        return;
      }
      if (!map.has(staffId)) {
        map.set(staffId, {
          staffId,
          staffName: record?.staff?.name || 'Unknown',
          position: record?.staff?.position || '—',
          imagePath: record?.staff?.imagePath || '',
          courses: new Map(),
          expectedRegistrations: 0,
          actualRegistrations: 0,
          hasActualRegistrations: false,
          expectedIncome: 0,
          actualIncome: 0,
          hasActualIncome: false,
        });
      }
      const entry = map.get(staffId);
      if (!entry.imagePath && record?.staff?.imagePath) {
        entry.imagePath = record.staff.imagePath;
      }
      const expectedReg = Number(record?.expectation?.registrations);
      if (Number.isFinite(expectedReg)) {
        entry.expectedRegistrations += expectedReg;
      }
      const actualReg = Number(record?.actuals?.registrations);
      if (Number.isFinite(actualReg)) {
        entry.actualRegistrations += actualReg;
        entry.hasActualRegistrations = true;
      }
      const expectedIncome = Number(record?.expectation?.income);
      if (Number.isFinite(expectedIncome)) {
        entry.expectedIncome += expectedIncome;
      }
      const actualIncome = Number(record?.actuals?.income);
      if (Number.isFinite(actualIncome)) {
        entry.actualIncome += actualIncome;
        entry.hasActualIncome = true;
      }
      const courseId = record?.courseCode?.id || record?.staffCourseMappingId;
      if (courseId && !entry.courses.has(courseId)) {
        const shortName = record?.courseCode?.shortName?.trim();
        const fullName = record?.courseCode?.fullName?.trim();
        entry.courses.set(courseId, shortName || fullName || `Course ${courseId}`);
      }
    });

    return Array.from(map.values())
      .map((entry) => {
        const expectedIncome = entry.expectedIncome || 0;
        const actualIncome = entry.hasActualIncome ? entry.actualIncome : null;
        const achievementRate = expectedIncome > 0 && actualIncome !== null
          ? (actualIncome / expectedIncome) * 100
          : null;
        return {
          staffId: entry.staffId,
          staffName: entry.staffName,
          position: entry.position,
          staffImage: entry.imagePath,
          courses: Array.from(entry.courses.values()),
          expectedRegistrations: entry.expectedRegistrations,
          actualRegistrations: entry.hasActualRegistrations ? entry.actualRegistrations : null,
          expectedIncome,
          actualIncome,
          achievementRate,
          status: resolveAchievementStatus(achievementRate),
        };
      })
      .sort((a, b) => {
        const aIncome = a.actualIncome === null ? -Infinity : a.actualIncome;
        const bIncome = b.actualIncome === null ? -Infinity : b.actualIncome;
        if (bIncome !== aIncome) {
          return bIncome - aIncome;
        }
        return a.staffName.localeCompare(b.staffName);
      });
  }, [performanceRecords]);

  const legendFormatter = (value) => {
    if (value === 'expectedIncome') {
      return 'Expected Income';
    }
    if (value === 'actualIncome') {
      return 'Actual Income';
    }
    if (value === 'achievementRate') {
      return 'Achievement Rate';
    }
    return value;
  };

  const tooltipFormatter = (value, name) => {
    if (name === 'achievementRate') {
      return [formatPercent(value), legendFormatter(name)];
    }
    return [formatCurrency(value), legendFormatter(name)];
  };

  const handleApply = (event) => {
    event.preventDefault();
    const nextFilters = {
      financialYear: filters.financialYear,
      quarters: [...filters.quarters],
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
    setAppliedFilters(nextFilters);
    loadSummary();
    loadPerformanceData();
  };

  const handleShowCourses = useCallback((row) => {
    if (!row?.courses?.length) {
      return;
    }
    setCourseModalData(row);
  }, []);

  const handleCloseCourseModal = useCallback(() => {
    setCourseModalData(null);
  }, []);

  const handleExport = () => {
    // Export wiring will plug into the reporting API once it is ready.
    console.info('Exporting target summary with filters', appliedFilters);
  };

  return (
    <AdminSalesLayout title="Targets Summary" currentSection="targetsSummary">
      <PageSection>
        <Card>
          <FilterHeader>
            <FilterIcon />
            <FilterStack>
              <FilterTitle>Filters</FilterTitle>
              <FilterSubtitle>Slice performance by financial year, quarter, or custom dates.</FilterSubtitle>
            </FilterStack>
          </FilterHeader>
          <FilterForm onSubmit={handleApply}>
            <FilterControl>
              <FilterLabel htmlFor="financial-year">Financial Year</FilterLabel>
              <SelectInput
                id="financial-year"
                value={filters.financialYear}
                onChange={(event) => setFilters((prev) => ({ ...prev, financialYear: event.target.value }))}
              >
                {financialYearOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </SelectInput>
            </FilterControl>
            <FilterControl>
              <FilterLabel>Quarter(s)</FilterLabel>
              <Select
                isMulti
                closeMenuOnSelect={false}
                placeholder="Select Quarter(s)"
                value={filters.quarters}
                onChange={(value) => setFilters((prev) => ({ ...prev, quarters: value || [] }))}
                options={quarterOptions}
                styles={selectStyles}
              />
            </FilterControl>
            <FilterControl>
              <FilterLabel htmlFor="start-date">Custom Start Date</FilterLabel>
              <DateInput
                id="start-date"
                type="date"
                value={filters.startDate}
                onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))}
                max={filters.endDate || undefined}
              />
            </FilterControl>
            <FilterControl>
              <FilterLabel htmlFor="end-date">Custom End Date</FilterLabel>
              <DateInput
                id="end-date"
                type="date"
                value={filters.endDate}
                onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))}
                min={filters.startDate || undefined}
              />
            </FilterControl>
            <ActionGroup>
              <ActionButton type="submit">Apply Filters</ActionButton>
              <ExportButton type="button" onClick={handleExport}>Export</ExportButton>
            </ActionGroup>
          </FilterForm>
        </Card>

        <AppliedFiltersCard>
          <AppliedFiltersTitle>Active filters</AppliedFiltersTitle>
          <FilterPill>{appliedFilters.financialYear || 'Pick a financial year'}</FilterPill>
          <FilterPill>{quarterText}</FilterPill>
          <FilterPill>{formatRangeLabel(appliedFilters.startDate, appliedFilters.endDate)}</FilterPill>
        </AppliedFiltersCard>

        {summaryError && (
          <ErrorBanner role="alert">{summaryError}</ErrorBanner>
        )}

        {summaryLoading ? (
          <PlaceholderPanel>
            <PlaceholderTitle>Loading summary</PlaceholderTitle>
            <PlaceholderText>Pulling the latest monthly target figures.</PlaceholderText>
          </PlaceholderPanel>
        ) : metricCards.length > 0 ? (
          <MetricGrid>
            {metricCards.map((metric) => {
              const Icon = metric.icon;
              return (
                <MetricCard key={metric.label} $accentBorder={metric.accentBorder}>
                  <MetricIcon $accent={metric.accent} $accentBg={metric.accentBg}>
                    <Icon />
                  </MetricIcon>
                  <MetricValue>{metric.value}</MetricValue>
                  <MetricLabel>{metric.label}</MetricLabel>
                  <MetricHint>{metric.hint}</MetricHint>
                </MetricCard>
              );
            })}
          </MetricGrid>
        ) : (
          <PlaceholderPanel>
            <PlaceholderTitle>No summary data yet</PlaceholderTitle>
            <PlaceholderText>Create monthly targets to view portfolio-wide progress.</PlaceholderText>
          </PlaceholderPanel>
        )}

        {performanceError && (
          <ErrorBanner role="alert">{performanceError}</ErrorBanner>
        )}

        <ChartCard>
          <ChartHeader>
            <ChartTitle>Expected vs Actual Income</ChartTitle>
            <ChartSubtitle>Stack counselors to see how actual collections track against planned income.</ChartSubtitle>
          </ChartHeader>
          {performanceLoading ? (
            <ChartEmpty>Loading counselor performance...</ChartEmpty>
          ) : performanceError ? (
            <ChartEmpty>Unable to display counselor performance.</ChartEmpty>
          ) : staffChartData.length > 0 ? (
            <ChartWrapper>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={staffChartData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="staffName" tick={{ fontSize: 12 }} interval={0} angle={-10} height={60} tickMargin={10} />
                  <YAxis tickFormatter={(value) => formatCurrencyShort(value)} tick={{ fontSize: 12 }} width={90} />
                  <Tooltip formatter={tooltipFormatter} labelFormatter={(label) => label} />
                  <Legend formatter={legendFormatter} />
                  <Bar dataKey="expectedIncome" fill="#60a5fa" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="expectedIncome" position="top" formatter={(value) => formatCurrency(value)} fontSize={11} fill="#1e3a8a" />
                  </Bar>
                  <Bar dataKey="actualIncome" fill="#34d399" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="actualIncome" position="top" formatter={(value) => formatCurrency(value)} fontSize={11} fill="#065f46" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartWrapper>
          ) : (
            <ChartEmpty>No counselor income data available.</ChartEmpty>
          )}
        </ChartCard>

        <Card>
          <ChartHeader>
            <ChartHeaderRow>
              <ChartHeaderCol>
                <ChartTitle>Monthly Performance by Course</ChartTitle>
                <ChartSubtitle>Month-wise performance trends for each course in the selected period.</ChartSubtitle>
              </ChartHeaderCol>
              <ChartFilterGroup>
                <ChartFilterLabel htmlFor="course-performance-select">Course filter</ChartFilterLabel>
                <ChartSelect
                  id="course-performance-select"
                  value={trendCourseFilter}
                  onChange={(event) => setTrendCourseFilter(event.target.value)}
                >
                  {trendCourseOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </ChartSelect>
              </ChartFilterGroup>
            </ChartHeaderRow>
          </ChartHeader>
          {!coursePerformanceSummary ? (
            <ChartEmpty>No performance data available for this course.</ChartEmpty>
          ) : (
            <>
              <CourseHeader>
                <CourseTitleGroup>
                  <CourseTitleText>
                    <CourseIcon aria-hidden="true">🎓</CourseIcon>
                    {coursePerformanceSummary.courseName}
                  </CourseTitleText>
                  <CourseMetaRow>
                    <CourseMetaItem>
                      Total Achievement:
                      <CourseMetaValue>{formatPercent(coursePerformanceSummary.achievementRate)}</CourseMetaValue>
                    </CourseMetaItem>
                    <CourseMetaItem>
                      Revenue:
                      <CourseMetaValue>
                        {coursePerformanceSummary.actualIncome === null
                          ? '—'
                          : formatCurrency(coursePerformanceSummary.actualIncome)}
                      </CourseMetaValue>
                      {coursePerformanceSummary.expectedIncome ? (
                        <CourseMetaValue style={{ color: '#64748b', fontWeight: 500 }}>
                          / {formatCurrency(coursePerformanceSummary.expectedIncome)} expected
                        </CourseMetaValue>
                      ) : null}
                    </CourseMetaItem>
                    <CourseMetaItem>
                      Staff:
                      <CourseMetaValue>{formatNumber(coursePerformanceSummary.staffCount)}</CourseMetaValue>
                    </CourseMetaItem>
                  </CourseMetaRow>
                </CourseTitleGroup>
                <CourseBadge>{formatPercent(coursePerformanceSummary.achievementRate)}</CourseBadge>
              </CourseHeader>
              {coursePerformanceTrendData.length > 0 ? (
                <CourseChartWrapper>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={coursePerformanceTrendData} margin={{ top: 20, right: 40, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} tickMargin={12} />
                      <YAxis yAxisId="income" tickFormatter={(value) => formatCurrencyShort(value)} tick={{ fontSize: 12 }} width={90} />
                      <YAxis
                        yAxisId="rate"
                        orientation="right"
                        tickFormatter={(value) => formatPercent(value)}
                        tick={{ fontSize: 12 }}
                        width={70}
                      />
                      <Tooltip formatter={tooltipFormatter} labelFormatter={(label) => label} />
                      <Legend formatter={legendFormatter} />
                      <Bar yAxisId="income" dataKey="expectedIncome" fill="#fb923c" radius={[6, 6, 0, 0]} />
                      <Bar yAxisId="income" dataKey="actualIncome" fill="#38bdf8" radius={[6, 6, 0, 0]} />
                      <Line yAxisId="rate" type="monotone" dataKey="achievementRate" stroke="#ec4899" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CourseChartWrapper>
              ) : (
                <ChartEmpty>No monthly breakdown yet for this course.</ChartEmpty>
              )}
            </>
          )}
        </Card>

        <ChartCard>
          <ChartHeader>
            <ChartTitle>Monthly Income Trend</ChartTitle>
            <ChartSubtitle>Compare expected vs actual income over recent months to spot momentum shifts.</ChartSubtitle>
          </ChartHeader>
          {performanceLoading ? (
            <ChartEmpty>Loading monthly income trend...</ChartEmpty>
          ) : performanceError ? (
            <ChartEmpty>Unable to display monthly income trend.</ChartEmpty>
          ) : monthlyTrendData.length > 0 ? (
            <ChartWrapper>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyTrendData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} tickMargin={12} />
                  <YAxis tickFormatter={(value) => formatCurrencyShort(value)} tick={{ fontSize: 12 }} width={90} />
                  <Tooltip formatter={tooltipFormatter} labelFormatter={(label) => label} />
                  <Legend formatter={legendFormatter} />
                  <Line type="monotone" dataKey="expectedIncome" stroke="#2563eb" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="actualIncome" stroke="#16a34a" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartWrapper>
          ) : (
            <ChartEmpty>No monthly trend data available.</ChartEmpty>
          )}
        </ChartCard>

        <Card>
          <ChartHeader>
            <ChartTitle>Detailed Staff Performance</ChartTitle>
            <ChartSubtitle>Review counselor-level progress with cumulative registrations, income, and achievement status.</ChartSubtitle>
          </ChartHeader>
          {performanceLoading ? (
            <ChartEmpty>Loading staff performance details...</ChartEmpty>
          ) : performanceError ? (
            <ChartEmpty>Unable to display staff performance details.</ChartEmpty>
          ) : staffDetailRows.length > 0 ? (
            <TableWrapper>
              <DetailTable>
                <thead>
                  <tr>
                    <DetailHeadCell>#</DetailHeadCell>
                    <StaffDetailHeadCell>Staff</StaffDetailHeadCell>
                    <DetailHeadCell>Position</DetailHeadCell>
                    <DetailHeadCell>Courses</DetailHeadCell>
                    <DetailHeadCell>Expected Registrations</DetailHeadCell>
                    <DetailHeadCell>Actual Registrations</DetailHeadCell>
                    <DetailHeadCell>Expected Income</DetailHeadCell>
                    <DetailHeadCell>Actual Income</DetailHeadCell>
                    <DetailHeadCell>Achievement Rate</DetailHeadCell>
                    <DetailHeadCell>Status</DetailHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {staffDetailRows.map((row, index) => (
                    <DetailRow key={row.staffId}>
                      <DetailCell>{index + 1}</DetailCell>
                        <StaffDetailCell>
                          <StaffIdentity>
                            <StaffImageWrapper title={row.staffName}>
                              {row.staffImage ? (
                                <StaffImage src={row.staffImage} alt={row.staffName ? `${row.staffName} profile` : 'Staff profile'} />
                              ) : (
                                getStaffInitials(row.staffName)
                              )}
                            </StaffImageWrapper>
                            <StaffInfo>
                              <span>{row.staffName}</span>
                              <StaffMeta>{row.courses.length ? `${row.courses.length} course${row.courses.length > 1 ? 's' : ''}` : 'No courses mapped'}</StaffMeta>
                            </StaffInfo>
                          </StaffIdentity>
                        </StaffDetailCell>
                      <DetailCell>{row.position || '—'}</DetailCell>
                      <DetailCell>
                        {row.courses.length ? (
                          <CourseToggleButton
                            type="button"
                            onClick={() => handleShowCourses(row)}
                            aria-haspopup="dialog"
                            aria-controls="course-modal"
                          >
                            {`${row.courses.length} course${row.courses.length > 1 ? 's' : ''}`}
                          </CourseToggleButton>
                        ) : '—'}
                      </DetailCell>
                      <DetailCell>{formatNumber(row.expectedRegistrations)}</DetailCell>
                      <DetailCell>{row.actualRegistrations === null ? '—' : formatNumber(row.actualRegistrations)}</DetailCell>
                      <DetailCell>{formatCurrency(row.expectedIncome)}</DetailCell>
                      <DetailCell>{row.actualIncome === null ? '—' : formatCurrency(row.actualIncome)}</DetailCell>
                      <DetailCell>{formatPercent(row.achievementRate)}</DetailCell>
                      <DetailCell>
                        <StatusBadge $tone={row.status.tone}>{row.status.label}</StatusBadge>
                      </DetailCell>
                    </DetailRow>
                  ))}
                </tbody>
              </DetailTable>
            </TableWrapper>
          ) : (
            <ChartEmpty>No staff performance details available.</ChartEmpty>
          )}
        </Card>
      </PageSection>
      {courseModalData && (
        <ModalBackdrop onClick={handleCloseCourseModal} role="presentation">
          <ModalPanel
            id="course-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="course-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle id="course-modal-title">{courseModalData.staffName}</ModalTitle>
              <ModalCloseButton type="button" onClick={handleCloseCourseModal} aria-label="Close course list">
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalSubtitle>
                Assigned courses ({courseModalData.courses.length})
              </ModalSubtitle>
              {courseModalData.courses.length ? (
                <CourseList>
                  {courseModalData.courses.map((course) => (
                    <CourseItem key={course}>{course}</CourseItem>
                  ))}
                </CourseList>
              ) : (
                <ModalSubtitle>No courses mapped yet.</ModalSubtitle>
              )}
            </ModalBody>
          </ModalPanel>
        </ModalBackdrop>
      )}
    </AdminSalesLayout>
  );
}
